import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import useAuth from "../hooks/useAuth";
import scoringTransparencyApi from "../api/scoringTransparencyApi";
import { buildScoringComponentMetadata } from "../utils/scoringComponentMetadata";
import {
  calculateDistributionStats,
  calculateHistogramBins,
  finiteNumber,
} from "../utils/scoringStatistics";

const DISPLAY_MODES = [
  { value: "scores", label: "Scores" },
  { value: "raw_values", label: "Raw Values" },
  { value: "effective_weights", label: "Effective Weights" },
  { value: "eligibility", label: "Eligibility" },
];

const VALUE_TYPES = [
  { value: "score", label: "Score" },
  { value: "raw_value", label: "Raw Value" },
  { value: "effective_weight", label: "Effective Weight" },
];

const PRESETS = [
  { value: "high_level", label: "High Level" },
  { value: "groups", label: "Groups" },
  { value: "all", label: "All Components" },
];

const COMPANY_COLUMNS = [
  { key: "company_name", label: "Company", width: 220 },
  { key: "ticker", label: "Ticker", width: 90 },
  { key: "primary_commodity", label: "Commodity", width: 130 },
  { key: "development_stage", label: "Stage", width: 130 },
  { key: "market_cap", label: "Market Cap", width: 130 },
];

const DEFAULT_TABLE_STATE = {
  mode: "scores",
  preset: "groups",
  ordering: "-overall",
  page: 1,
  pageSize: 100,
  focusedComponent: "overall",
  filters: {
    search: "",
    development_stage: "",
    min_market_cap: "",
    max_market_cap: "",
    min_overall_score: "",
    max_overall_score: "",
    eligibility: "",
    exclusion_reason: "",
  },
};

const DEFAULT_SEGMENT_FILTERS = {
  development_stage: "",
  min_market_cap: "",
  max_market_cap: "",
};

function formatRunLabel(run) {
  if (!run) return "";
  const date = run.created_at ? new Date(run.created_at).toLocaleString() : "Unknown date";
  const month = run.score_month ? ` - ${run.score_month.slice(0, 7)}` : "";
  return `${date} - ${run.model_version_code} - Run ${run.id}${month}`;
}

function formatNumber(value, digits = 1) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

function formatMarketCap(value) {
  const numeric = finiteNumber(value);
  if (numeric === null) return "-";
  if (numeric >= 1_000_000_000) return `$${(numeric / 1_000_000_000).toFixed(1)}B`;
  if (numeric >= 1_000_000) return `$${(numeric / 1_000_000).toFixed(1)}M`;
  return `$${numeric.toLocaleString()}`;
}

function formatValue(value, type = "score") {
  const numeric = finiteNumber(value);
  if (numeric === null) return "-";
  if (type === "effective_weight") return `${formatNumber(numeric * 100, 2)}%`;
  if (type === "raw_value") return formatNumber(numeric, 4);
  return formatNumber(numeric, 1);
}

function displayComponentValue(component, mode) {
  if (!component) return "-";
  if (mode === "raw_values") return formatValue(component.raw_value, "raw_value");
  if (mode === "effective_weights") return formatValue(component.effective_weight, "effective_weight");
  if (mode === "eligibility") {
    if (component.is_eligible === false) return "Ineligible";
    if (component.score === null || component.score === undefined) return "Missing";
    return "Eligible";
  }
  if (component.is_eligible === false) return "Ineligible";
  return formatValue(component.score, "score");
}

function cleanParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter((entry) => entry[1] !== "" && entry[1] !== null && entry[1] !== undefined)
  );
}

function componentSortKey(componentCode, ordering) {
  if (ordering === componentCode) return `-${componentCode}`;
  return componentCode;
}

function metadataFromPayload(payload) {
  return buildScoringComponentMetadata(payload?.components || []);
}

function valuesForComponent(matrixPayload, componentCode) {
  return (matrixPayload?.results || []).map((row) => ({
    company: row.company,
    value: row.values?.[componentCode],
    isEligible: row.eligibility?.[componentCode]?.is_eligible,
    exclusionReason: row.eligibility?.[componentCode]?.exclusion_reason,
  }));
}

function countsForComponent(values) {
  return {
    total_companies: values.length,
    valid: values.filter((item) => finiteNumber(item.value) !== null).length,
    missing: values.filter((item) => finiteNumber(item.value) === null).length,
    ineligible: values.filter((item) => item.isEligible === false).length,
  };
}

function buildDistributionRows(matrixPayload, componentMeta) {
  return componentMeta.orderedComponents
    .filter((component) => ["overall", "pillar", "metric"].includes(component.level))
    .map((component) => {
      const values = valuesForComponent(matrixPayload, component.code);
      const stats = calculateDistributionStats(values.map((item) => item.value), values.length);
      const counts = countsForComponent(values);
      return { component, stats, counts };
    })
    .sort((left, right) => {
      const levelRank = { overall: 0, pillar: 1, metric: 2 };
      return (
        (levelRank[left.component.level] ?? 9) - (levelRank[right.component.level] ?? 9) ||
        (left.component.display_order ?? 10000) - (right.component.display_order ?? 10000) ||
        (right.counts.valid || 0) - (left.counts.valid || 0)
      );
    });
}

export default function ScoringTransparencyPage() {
  const { user, loadingUser } = useAuth();
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [activeTab, setActiveTab] = useState("company-table");
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [runError, setRunError] = useState("");
  const [tableState, setTableState] = useState(DEFAULT_TABLE_STATE);
  const [tablePayload, setTablePayload] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const [exporting, setExporting] = useState(false);

  const isInternal = Boolean(user?.is_staff || user?.profile?.role === "admin");
  const selectedRun = useMemo(
    () => runs.find((run) => String(run.id) === String(selectedRunId)),
    [runs, selectedRunId]
  );

  const componentMeta = useMemo(() => metadataFromPayload(tablePayload), [tablePayload]);

  useEffect(() => {
    let active = true;
    async function loadRuns() {
      setLoadingRuns(true);
      setRunError("");
      try {
        const data = await scoringTransparencyApi.listRuns();
        if (!active) return;
        setRuns(data);
        setSelectedRunId((current) => current || (data[0]?.id ? String(data[0].id) : ""));
      } catch (err) {
        if (active) setRunError(err?.response?.data?.detail || "Unable to load scoring runs.");
      } finally {
        if (active) setLoadingRuns(false);
      }
    }
    if (!loadingUser && isInternal) loadRuns();
    return () => {
      active = false;
    };
  }, [loadingUser, isInternal]);

  const tableParams = useMemo(
    () =>
      cleanParams({
        scoring_run_id: selectedRunId,
        ...tableState.filters,
        component_code: tableState.focusedComponent,
        ordering: tableState.ordering,
        mode: tableState.mode,
        page: tableState.page,
        page_size: tableState.pageSize,
      }),
    [selectedRunId, tableState]
  );

  const loadTable = useCallback(async () => {
    if (!selectedRunId) return;
    setTableLoading(true);
    setTableError("");
    try {
      const data = await scoringTransparencyApi.getTransparency(tableParams);
      setTablePayload(data);
    } catch (err) {
      setTableError(err?.response?.data?.detail || "Unable to load scoring transparency data.");
    } finally {
      setTableLoading(false);
    }
  }, [selectedRunId, tableParams]);

  useEffect(() => {
    if (!loadingUser && isInternal && selectedRunId) loadTable();
  }, [loadingUser, isInternal, selectedRunId, loadTable]);

  useEffect(() => {
    setTablePayload(null);
    setTableState((current) => ({ ...current, page: 1 }));
  }, [selectedRunId]);

  if (loadingUser) return <LinearProgress />;

  if (!isInternal) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Scoring Transparency is available to internal staff users only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: "100%" }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Scoring Transparency</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 920 }}>
            Internal model analysis for company-level score inspection and component distributions.
          </Typography>
        </Box>

        {runError ? <Alert severity="error">{runError}</Alert> : null}
        <ScoringRunSelector
          runs={runs}
          selectedRunId={selectedRunId}
          selectedRun={selectedRun}
          loading={loadingRuns}
          onChange={setSelectedRunId}
          payload={tablePayload}
        />

        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_event, value) => setActiveTab(value)}
            sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Company Table" value="company-table" />
            <Tab label="Component Distributions" value="component-distributions" />
          </Tabs>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: activeTab === "company-table" ? "block" : "none" }}>
              <CompanyTableTab
                payload={tablePayload}
                loading={tableLoading}
                error={tableError}
                state={tableState}
                setState={setTableState}
                componentMeta={componentMeta}
                reload={loadTable}
                exporting={exporting}
                setExporting={setExporting}
                tableParams={tableParams}
              />
            </Box>
            <Box sx={{ display: activeTab === "component-distributions" ? "block" : "none" }}>
              <ComponentDistributionsTab
                selectedRunId={selectedRunId}
                componentMeta={componentMeta}
                filterOptions={tablePayload?.filters}
              />
            </Box>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}

function ScoringRunSelector({ runs, selectedRunId, selectedRun, loading, onChange, payload }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 430 } }}>
          <InputLabel>Scoring Run</InputLabel>
          <Select label="Scoring Run" value={selectedRunId} onChange={(event) => onChange(event.target.value)}>
            {runs.map((run) => (
              <MenuItem key={run.id} value={String(run.id)}>
                {formatRunLabel(run)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading ? <CircularProgress size={22} /> : null}
        {selectedRun ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" label={`Model: ${selectedRun.model_version_code}`} />
            <Chip size="small" label={`Run: ${selectedRun.id}`} />
            <Chip size="small" label={`Source: ${selectedRun.source}`} />
            {payload?.run ? <Chip size="small" label={`Companies: ${payload.run.company_count}`} /> : null}
            {payload?.run ? <Chip size="small" label={`Components: ${payload.run.component_count}`} /> : null}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function CompanyTableTab({ payload, loading, error, state, setState, componentMeta, reload, exporting, setExporting, tableParams }) {
  const components = componentMeta.orderedComponents;
  const visibleComponents = useMemo(() => {
    if (state.preset === "high_level") return components.filter((component) => ["overall", "pillar"].includes(component.level));
    if (state.preset === "all") return components;
    return components.filter((component) => ["overall", "pillar", "group"].includes(component.level));
  }, [components, state.preset]);
  const visibleWithFocus = useMemo(() => {
    if (!state.focusedComponent || visibleComponents.some((component) => component.code === state.focusedComponent)) {
      return visibleComponents;
    }
    const focused = componentMeta.byCode[state.focusedComponent];
    return focused ? [...visibleComponents, focused] : visibleComponents;
  }, [componentMeta.byCode, state.focusedComponent, visibleComponents]);

  const updateState = (updates) => setState((current) => ({ ...current, ...updates }));
  const updateFilters = (updates) =>
    setState((current) => ({ ...current, page: 1, filters: { ...current.filters, ...updates } }));

  const handleExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await scoringTransparencyApi.exportCsv({
        ...tableParams,
        mode: state.mode,
        columns: visibleWithFocus.map((component) => component.code).join(","),
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setState((current) => ({ ...current }));
    } finally {
      setExporting(false);
    }
  };

  const pagination = payload?.pagination || { page: 1, page_size: 100, total_rows: 0 };

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>{error}</Alert> : null}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ lg: "center" }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Display</InputLabel>
          <Select label="Display" value={state.mode} onChange={(event) => updateState({ mode: event.target.value, page: 1 })}>
            {DISPLAY_MODES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Columns</InputLabel>
          <Select label="Columns" value={state.preset} onChange={(event) => updateState({ preset: event.target.value })}>
            {PRESETS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Focus Component</InputLabel>
          <Select
            label="Focus Component"
            value={state.focusedComponent}
            onChange={(event) => updateState({ focusedComponent: event.target.value, ordering: `-${event.target.value}`, page: 1 })}
          >
            {componentMeta.selectOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button startIcon={<DownloadIcon />} variant="contained" onClick={handleExport} disabled={exporting || !payload}>
          Export CSV
        </Button>
      </Stack>

      <FilterToolbar filters={state.filters} filterOptions={payload?.filters} onChange={updateFilters} />

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }} variant="outlined">
        {loading ? <LinearProgress /> : null}
        <TableContainer sx={{ maxHeight: "72vh", overflow: "auto" }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {COMPANY_COLUMNS.map((column, index) => (
                  <TableCell
                    key={column.key}
                    onClick={() => updateState({ ordering: state.ordering === column.key ? `-${column.key}` : column.key, page: 1 })}
                    sx={{
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      position: index === 0 ? "sticky" : "static",
                      left: index === 0 ? 0 : "auto",
                      zIndex: index === 0 ? 4 : 3,
                      backgroundColor: "background.paper",
                      minWidth: column.width,
                      cursor: "pointer",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {visibleWithFocus.map((component) => (
                  <Tooltip key={component.code} title={`${componentMeta.hierarchyLabel(component)} (${component.code})`}>
                    <TableCell
                      onClick={() => {
                        if (state.mode !== "eligibility") {
                          updateState({ ordering: componentSortKey(component.code, state.ordering), page: 1 });
                        }
                      }}
                      sx={{
                        fontWeight: 800,
                        minWidth: 130,
                        whiteSpace: "nowrap",
                        cursor: state.mode === "eligibility" ? "default" : "pointer",
                      }}
                    >
                      {component.name}
                    </TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(payload?.results || []).map((row) => (
                <TableRow key={row.company.id} hover>
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 2, backgroundColor: "background.paper", minWidth: 220 }}>
                    <Button component={RouterLink} to={`/companies/${row.company.slug}`} size="small" sx={{ px: 0, justifyContent: "flex-start" }}>
                      {row.company.name}
                    </Button>
                  </TableCell>
                  <TableCell>{row.company.ticker || "-"}</TableCell>
                  <TableCell>{row.company.primary_commodity || "-"}</TableCell>
                  <TableCell>{row.company.development_stage || "-"}</TableCell>
                  <TableCell>{formatMarketCap(row.company.market_cap)}</TableCell>
                  {visibleWithFocus.map((component) => {
                    const score = row.scores?.[component.code];
                    const value = displayComponentValue(score, state.mode);
                    return (
                      <Tooltip key={component.code} title={score?.exclusion_reason || ""}>
                        <TableCell sx={{ whiteSpace: "nowrap", color: value === "Ineligible" ? "text.secondary" : "text.primary" }}>
                          {value}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              ))}
              {!loading && payload && !payload.results?.length ? (
                <TableRow>
                  <TableCell colSpan={COMPANY_COLUMNS.length + visibleWithFocus.length}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography>No companies match the current filters.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={pagination.total_rows || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={pagination.page_size || 100}
          rowsPerPageOptions={[50, 100, 250]}
          onPageChange={(_event, nextPage) => updateState({ page: nextPage + 1 })}
          onRowsPerPageChange={(event) => updateState({ pageSize: event.target.value, page: 1 })}
        />
      </Paper>
    </Stack>
  );
}

function FilterToolbar({ filters, filterOptions, onChange }) {
  const reset = () => onChange({ ...DEFAULT_TABLE_STATE.filters });
  return (
    <Paper sx={{ p: 1.5, borderRadius: 2 }} variant="outlined">
      <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25}>
        <TextField size="small" label="Search company or ticker" value={filters.search} onChange={(event) => onChange({ search: event.target.value })} />
        <OptionSelect label="Lifecycle" value={filters.development_stage} options={filterOptions?.development_stages} onChange={(value) => onChange({ development_stage: value })} />
        <TextField size="small" label="Min market cap" type="number" value={filters.min_market_cap} onChange={(event) => onChange({ min_market_cap: event.target.value })} />
        <TextField size="small" label="Max market cap" type="number" value={filters.max_market_cap} onChange={(event) => onChange({ max_market_cap: event.target.value })} />
        <TextField size="small" label="Min overall" type="number" value={filters.min_overall_score} onChange={(event) => onChange({ min_overall_score: event.target.value })} />
        <TextField size="small" label="Max overall" type="number" value={filters.max_overall_score} onChange={(event) => onChange({ max_overall_score: event.target.value })} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Eligibility</InputLabel>
          <Select label="Eligibility" value={filters.eligibility || "all"} onChange={(event) => onChange({ eligibility: event.target.value === "all" ? "" : event.target.value })}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="has_score">Has score</MenuItem>
            <MenuItem value="missing_score">Missing score</MenuItem>
            <MenuItem value="any_ineligible">Any ineligible component</MenuItem>
          </Select>
        </FormControl>
        <OptionSelect label="Exclusion reason" value={filters.exclusion_reason} options={filterOptions?.exclusion_reasons} onChange={(value) => onChange({ exclusion_reason: value })} />
        <Button startIcon={<RestartAltIcon />} onClick={reset}>Reset</Button>
      </Stack>
    </Paper>
  );
}

function OptionSelect({ label, value, options = [], onChange }) {
  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || ""} onChange={(event) => onChange(event.target.value)}>
        <MenuItem value="">All</MenuItem>
        {(options || []).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function SegmentFilters({ filters, filterOptions, onChange }) {
  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25}>
      <OptionSelect label="Lifecycle" value={filters.development_stage} options={filterOptions?.development_stages} onChange={(value) => onChange({ development_stage: value })} />
      <TextField size="small" label="Min market cap" type="number" value={filters.min_market_cap} onChange={(event) => onChange({ min_market_cap: event.target.value })} />
      <TextField size="small" label="Max market cap" type="number" value={filters.max_market_cap} onChange={(event) => onChange({ max_market_cap: event.target.value })} />
    </Stack>
  );
}

function ComponentDistributionsTab({ selectedRunId, componentMeta, filterOptions }) {
  const [state, setState] = useState({ componentCode: "overall", valueType: "score", filters: DEFAULT_SEGMENT_FILTERS });
  const [matrixPayload, setMatrixPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(
    () =>
      cleanParams({
        scoring_run_id: selectedRunId,
        value_type: state.valueType,
        ...state.filters,
      }),
    [selectedRunId, state.filters, state.valueType]
  );

  const loadDistributionMatrix = useCallback(async () => {
    if (!selectedRunId) return;
    setLoading(true);
    setError("");
    try {
      setMatrixPayload(await scoringTransparencyApi.getComponentDistributionMatrix(params));
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load component distributions.");
    } finally {
      setLoading(false);
    }
  }, [params, selectedRunId]);

  useEffect(() => {
    setMatrixPayload(null);
    loadDistributionMatrix();
  }, [loadDistributionMatrix]);

  const distributionRows = useMemo(
    () => buildDistributionRows(matrixPayload, componentMeta),
    [componentMeta, matrixPayload]
  );
  useEffect(() => {
    if (!distributionRows.length) return;
    if (!distributionRows.some((row) => row.component.code === state.componentCode)) {
      setState((current) => ({ ...current, componentCode: distributionRows[0].component.code }));
    }
  }, [componentMeta.byCode, distributionRows, state.componentCode]);

  const selectedValues = useMemo(
    () => valuesForComponent(matrixPayload, state.componentCode),
    [matrixPayload, state.componentCode]
  );
  const rawValues = useMemo(() => selectedValues.map((item) => item.value), [selectedValues]);
  const stats = useMemo(
    () => calculateDistributionStats(rawValues, selectedValues.length),
    [rawValues, selectedValues.length]
  );
  const counts = useMemo(() => countsForComponent(selectedValues), [selectedValues]);
  const bins = useMemo(() => calculateHistogramBins(rawValues), [rawValues]);
  const selectedComponent = componentMeta.byCode[state.componentCode];
  const rawNotApplicable = state.valueType === "raw_value" && selectedComponent && selectedComponent.level !== "metric" && stats.validCount === 0;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" action={<Button onClick={loadDistributionMatrix}>Retry</Button>}>{error}</Alert> : null}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25}>
        <ValueTypeSelect value={state.valueType} onChange={(value) => setState((current) => ({ ...current, valueType: value }))} />
      </Stack>
      <SegmentFilters
        filters={state.filters}
        filterOptions={filterOptions}
        onChange={(updates) => setState((current) => ({ ...current, filters: { ...current.filters, ...updates } }))}
      />
      {loading ? <LinearProgress /> : null}
      <MetricDistributionTable
        rows={distributionRows}
        selectedCode={state.componentCode}
        valueType={state.valueType}
        onSelect={(componentCode) => setState((current) => ({ ...current, componentCode }))}
      />
      {rawNotApplicable ? <Alert severity="info">Raw values are not stored for this rolled-up component.</Alert> : null}
      {matrixPayload && !rawNotApplicable ? (
        <>
          <Typography variant="h6" fontWeight={800}>
            {selectedComponent?.name || state.componentCode}
          </Typography>
          <DistributionSummary stats={stats} counts={counts} valueType={state.valueType} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Histogram bins={bins} valueType={state.valueType} />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <PercentileTable stats={stats} valueType={state.valueType} />
            </Grid>
          </Grid>
        </>
      ) : null}
    </Stack>
  );
}

function ValueTypeSelect({ value, onChange }) {
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>Value Type</InputLabel>
      <Select label="Value Type" value={value} onChange={(event) => onChange(event.target.value)}>
        {VALUE_TYPES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function MetricDistributionTable({ rows, selectedCode, valueType, onSelect }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Score Component</TableCell>
              <TableCell align="right">Valid N</TableCell>
              <TableCell align="right">Missing %</TableCell>
              <TableCell align="right">Mean</TableCell>
              <TableCell align="right">Median</TableCell>
              <TableCell align="right">P10</TableCell>
              <TableCell align="right">P90</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.component.code}
                hover
                selected={row.component.code === selectedCode}
                onClick={() => onSelect(row.component.code)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Typography fontWeight={700}>{row.component.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.component.level} - {row.component.code}
                  </Typography>
                </TableCell>
                <TableCell align="right">{row.counts.valid}</TableCell>
                <TableCell align="right">{formatNumber(row.stats.missingPercentage, 1)}%</TableCell>
                <TableCell align="right">{formatValue(row.stats.mean, valueType)}</TableCell>
                <TableCell align="right">{formatValue(row.stats.median, valueType)}</TableCell>
                <TableCell align="right">{formatValue(row.stats.p10, valueType)}</TableCell>
                <TableCell align="right">{formatValue(row.stats.p90, valueType)}</TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={7}>No score components are available for this run and filter selection.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function DistributionSummary({ stats, counts, valueType = "score" }) {
  const items = [
    ["Total companies", counts?.total_companies],
    ["Valid values", counts?.valid],
    ["Missing", counts?.missing],
    ["Missing %", `${formatNumber(stats.missingPercentage, 1)}%`],
    ["Ineligible", counts?.ineligible],
    ["Mean", formatValue(stats.mean, valueType)],
    ["Std. dev.", formatValue(stats.standardDeviation, valueType)],
    ["Min", formatValue(stats.min, valueType)],
    ["Max", formatValue(stats.max, valueType)],
  ];
  return <StatGrid items={items} />;
}

function StatGrid({ items }) {
  return (
    <Grid container spacing={1.25}>
      {items.map(([label, value]) => (
        <Grid key={label} size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography fontWeight={800}>{value ?? "-"}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function Histogram({ bins, valueType }) {
  if (!bins.length) return <Alert severity="info">No valid values are available for this component and selection.</Alert>;
  const maxCount = Math.max(...bins.map((bin) => bin.count), 1);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1 }}>Distribution</Typography>
      <Box sx={{ height: 280, display: "flex", alignItems: "flex-end", gap: 0.75, borderBottom: 1, borderColor: "divider", pb: 1 }}>
        {bins.map((bin) => (
          <Tooltip key={`${bin.min}-${bin.max}`} title={`${formatValue(bin.min, valueType)} to ${formatValue(bin.max, valueType)}: ${bin.count}`}>
            <Box sx={{ flex: 1, minWidth: 10, height: `${Math.max(2, (bin.count / maxCount) * 100)}%`, bgcolor: "primary.main", borderRadius: "4px 4px 0 0" }} />
          </Tooltip>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">X-axis: selected value range. Y-axis: company count.</Typography>
    </Paper>
  );
}

function PercentileTable({ stats, valueType }) {
  const rows = [
    ["P5", stats.p5],
    ["P10", stats.p10],
    ["P25", stats.p25],
    ["P50", stats.median],
    ["P75", stats.p75],
    ["P90", stats.p90],
    ["P95", stats.p95],
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography fontWeight={800} sx={{ mb: 1 }}>Percentiles</Typography>
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell>{label}</TableCell>
              <TableCell align="right">{formatValue(value, valueType)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
